import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, UserPlus, ShoppingCart, Loader2, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNewSystem, setIsNewSystem] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Check if this is a new system (no users)
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await fetch(`${API}/api/auth/setup-status`);
        if (response.ok) {
          const data = await response.json();
          setIsNewSystem(!data.is_setup);
        }
      } catch (error) {
        console.log('Setup check failed');
      } finally {
        setCheckingSetup(false);
      }
    };
    checkSetup();
  }, []);

  // If system already initialized, registration is closed — redirect to login
  useEffect(() => {
    if (!checkingSetup && !isNewSystem) {
      toast.error('التسجيل العام مغلق. تواصل مع مدير النظام لإنشاء حساب جديد');
      navigate('/login', { replace: true });
    }
  }, [checkingSetup, isNewSystem, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('كلمة المرور غير متطابقة');
      return;
    }
    
    if (password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use initial-setup if this is first user
      await register(name, email, password, null, isNewSystem);
      toast.success(isNewSystem ? 'تم إنشاء حساب مدير النظام بنجاح!' : 'تم إنشاء الحساب بنجاح');
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4">
            {isNewSystem ? <Shield className="w-8 h-8 text-white" /> : <ShoppingCart className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-2xl font-bold text-gray-800">نظام إدارة المشاريع</h1>
          <p className="text-gray-500 mt-2">
            {isNewSystem ? 'إعداد النظام - إنشاء حساب مدير النظام' : 'إنشاء حساب جديد'}
          </p>
        </div>

        {/* Setup Notice */}
        {isNewSystem && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-800 font-medium mb-1">🎉 مرحباً بك!</p>
            <p className="text-sm text-amber-700">
              هذا النظام جديد. سيتم إنشاء حسابك كـ <strong>مدير النظام (Super Admin)</strong> وستتمكن من إدارة كل شيء.
            </p>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
            {isNewSystem ? 'إنشاء حساب مدير النظام' : 'تسجيل جديد'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                الاسم الكامل
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="أدخل اسمك الكامل"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                البريد الإلكتروني
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="example@company.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                كلمة المرور
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none pl-12"
                  placeholder="6 أحرف على الأقل"
                  required
                  disabled={loading}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                تأكيد كلمة المرور
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="أعد إدخال كلمة المرور"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري إنشاء الحساب...
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  إنشاء حساب
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              لديك حساب بالفعل؟{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          بالتسجيل، أنت توافق على شروط الاستخدام
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
